import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart3, Scale, Gauge, Trophy, Download } from "lucide-react";

const fmtPpw = (v) => (v == null ? "—" : `$${Number(v).toFixed(3)}/W`);
const fmtMh = (v) => (v == null ? "—" : `${Math.round(Number(v)).toLocaleString()}`);

// Which server grouping powers the table, and whether win-rate data exists for it.
const GROUPINGS = [
  { key: "by_scope", label: "By scope", hit: "by_scope" },
  { key: "by_material", label: "By material responsibility", hit: "by_material" },
  { key: "by_primary_discipline", label: "By primary discipline", hit: null },
  { key: "by_completeness", label: "By completeness", hit: null },
  { key: "by_size", label: "By size band", hit: null },
  { key: "by_state", label: "By state", hit: null },
];
const STORE_KEY = "benchmarks.groupBy";

function Kpi({ icon: Icon, accent, label, value, sub, testid }) {
  return (
    <Card className="p-4 flex items-center gap-3" data-testid={testid}>
      <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${accent}1a` }}>
        <Icon className="w-4 h-4" style={{ color: accent }} />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
        <div className="text-lg font-bold text-slate-900 font-mono tabular-nums truncate">{value}</div>
        <div className="text-[11px] text-slate-400 truncate">{sub}</div>
      </div>
    </Card>
  );
}

// Insights → Benchmarks: $/W, man-hours/MW and win rate. Saveable grouping filter + CSV export.
export default function Benchmarks() {
  const [bench, setBench] = useState(null);
  const [hit, setHit] = useState(null);
  const [groupBy, setGroupBy] = useState(() => localStorage.getItem(STORE_KEY) || "by_scope");
  useEffect(() => {
    api.get("/insights/benchmarks").then((r) => setBench(r.data)).catch(() => {});
    api.get("/insights/hit-rate").then((r) => setHit(r.data)).catch(() => {});
  }, []);
  useEffect(() => { localStorage.setItem(STORE_KEY, groupBy); }, [groupBy]);

  const minS = bench?.min_sample || 3;
  const hitMin = hit?.min_sample || 5;
  const grouping = GROUPINGS.find((g) => g.key === groupBy) || GROUPINGS[0];
  const hitMap = {};
  if (grouping.hit) (hit?.[grouping.hit] || []).forEach((r) => { hitMap[r.key] = r; });
  const rows = (bench?.[grouping.key] || []).map((r) => ({ ...r, hit: hitMap[r.key] }));

  const exportCsv = () => {
    const head = ["Group", "n", "$/W median", "$/W P25", "$/W P75", "MH/MW median", "MH/MW n", "Win rate %", "Won", "Decided"];
    const lines = [`Benchmarks — ${grouping.label}`, `Exported ${new Date().toISOString().slice(0, 10)}`,
      `Overall $/W median,${bench?.overall?.median ?? ""}`,
      `Overall MH/MW median,${bench?.overall?.mh_median ?? ""}`,
      `Overall win rate %,${hit?.overall?.rate_count ?? ""}`, "", head.join(",")];
    rows.forEach((r) => {
      const h = r.hit;
      const hthin = !h || h.decided < hitMin;
      lines.push([`"${(r.key || "").replace(/"/g, "'")}"`, r.count, r.median ?? "", r.p25 ?? "", r.p75 ?? "",
        r.mh_median ?? "", r.mh_count ?? "", hthin ? "" : h.rate_count, hthin ? "" : h.won, hthin ? "" : h.decided].join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `benchmarks-${groupBy.replace(/^by_/, "")}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="px-6 sm:px-8 py-8" data-testid="benchmarks-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-black flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#003E87]" /> Benchmarks
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">$/W, man-hours per MW and win rate — across your priced book and imported history. A mixed-scope job counts into each of its disciplines.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={groupBy} onValueChange={setGroupBy}>
            <SelectTrigger className="w-52" data-testid="bench-groupby"><SelectValue /></SelectTrigger>
            <SelectContent>
              {GROUPINGS.map((g) => <SelectItem key={g.key} value={g.key} data-testid={`bench-groupby-${g.key}`}>{g.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={exportCsv} disabled={!rows.length} data-testid="bench-export-csv">
            <Download className="w-4 h-4 mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      {!bench ? (
        <div className="text-sm text-slate-400 py-16 text-center">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" data-testid="bench-overall">
            <Kpi icon={Scale} accent="#003E87" label="$/W median" value={fmtPpw(bench.overall?.median)} sub={bench.overall?.count ? `${bench.overall.count} priced jobs` : "no data yet"} testid="bench-kpi-ppw" />
            <Kpi icon={Gauge} accent="#6366F1" label="MH/MW median" value={bench.overall?.mh_median != null ? fmtMh(bench.overall.mh_median) : "—"} sub={bench.overall?.mh_count ? `${bench.overall.mh_count} with man-hours` : "add man-hours to compare"} testid="bench-kpi-mh" />
            <Kpi icon={Trophy} accent="#F4C400" label="Win rate" value={hit?.overall?.decided ? `${hit.overall.rate_count}%` : "—"} sub={hit?.overall ? `${hit.overall.won} won · ${hit.overall.decided} decided` : ""} testid="bench-kpi-win" />
          </div>

          <Card className="p-5" data-testid="bench-table-card">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <h3 className="text-sm font-semibold text-black">{grouping.label}</h3>
              {!grouping.hit && <span className="text-[11px] text-slate-400">Win rate is only tracked by scope &amp; material</span>}
            </div>
            {groupBy === "by_material" && (
              <p className="text-xs text-slate-400 mb-3">$/W is only honest between jobs on the same material basis — a labor-only bid can’t be compared to a full-material EPC. This is why scope-level compare filters to the same band.</p>
            )}
            {rows.length === 0 ? (
              <div className="text-xs text-slate-400 py-6 text-center">No priced jobs for this grouping yet. Tag scopes on the pipeline to populate this.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[11px] uppercase tracking-wide text-slate-400 text-left border-b border-slate-200">
                      <th className="py-2 pr-2">{grouping.label.replace(/^By /, "")}</th>
                      <th className="py-2 px-2">n</th>
                      <th className="py-2 px-2">$/W median</th>
                      <th className="py-2 px-2">$/W band (P25–P75)</th>
                      <th className="py-2 px-2">MH/MW median</th>
                      <th className="py-2 px-2">Win rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const thin = r.count < minS;
                      const h = r.hit;
                      const hthin = !h || h.decided < hitMin;
                      return (
                        <tr key={r.key} className="border-b border-slate-50" data-testid={`bench-row-${String(r.key).replace(/\W+/g, "-").toLowerCase()}`}>
                          <td className="py-2 pr-2 text-slate-700 font-medium">{r.key}{thin ? <span className="text-[10px] text-slate-300 ml-1.5">· thin</span> : null}</td>
                          <td className="py-2 px-2 font-mono text-slate-500">{r.count}</td>
                          <td className="py-2 px-2 font-mono font-semibold text-[#003E87]">{fmtPpw(r.median)}</td>
                          <td className="py-2 px-2 font-mono text-slate-500">{fmtPpw(r.p25)} – {fmtPpw(r.p75)}</td>
                          <td className="py-2 px-2 font-mono text-slate-600">{r.mh_median != null ? fmtMh(r.mh_median) : "—"}<span className="text-[10px] text-slate-300 ml-1">{r.mh_median != null ? `(n ${r.mh_count})` : ""}</span></td>
                          <td className="py-2 px-2 font-mono">{!grouping.hit ? <span className="text-slate-300">—</span> : hthin ? <span className="text-slate-300">—</span> : <span className="text-slate-800">{h.rate_count}% <span className="text-slate-400 text-xs">{h.won}/{h.decided}</span></span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-[11px] text-slate-400 mt-3">Thin slices (fewer than {minS} priced, or {hitMin} decided for win rate) are marked and withheld rather than guessed. Your grouping choice is remembered, and Export CSV downloads exactly what you see.</p>
          </Card>
        </>
      )}
    </div>
  );
}
